import { getCurrentUser, logout } from './session.js';
import { applyThemePreference, getThemePreference, type ThemePreference } from '../ui/theme.js';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  moderator: 'Modérateur',
  player: 'Joueur',
};

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string }> = [
  { value: 'dark', label: 'Sombre' },
  { value: 'light', label: 'Clair' },
  { value: 'system', label: 'Système' },
];

const CHEVRON_PATH =
  'M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z';

function themeOptionIndex(pref: ThemePreference): number {
  const idx = THEME_OPTIONS.findIndex((opt) => opt.value === pref);
  return idx >= 0 ? idx : 0;
}

function nextThemePreference(pref: ThemePreference): ThemePreference {
  const next = (themeOptionIndex(pref) + 1) % THEME_OPTIONS.length;
  return THEME_OPTIONS[next].value;
}

function createChevron(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'menu-select__chevron');
  svg.setAttribute('viewBox', '0 0 360 360');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', CHEVRON_PATH);
  svg.appendChild(path);
  return svg;
}

function setMenuOpen(root: HTMLElement, trigger: HTMLButtonElement, open: boolean): void {
  root.classList.toggle('is-open', open);
  trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  const panel = root.querySelector<HTMLElement>('.menu-select__submenu');
  panel?.setAttribute('aria-hidden', open ? 'false' : 'true');
}

/** Souris/trackpad : hover fiable. Tactile : pas de hover (évite open+close au tap). */
export function prefersHoverMenu(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function syncThemeCycleButton(host: HTMLElement): void {
  const btn = host.querySelector<HTMLButtonElement>('.app-nav-account-theme-cycle');
  if (!btn) return;
  const pref = getThemePreference();
  const opt = THEME_OPTIONS[themeOptionIndex(pref)];
  btn.dataset.themePref = opt.value;
  const label = btn.querySelector('.app-nav-account-theme-cycle-label');
  if (label) label.textContent = opt.label;
  btn.setAttribute('aria-label', `Thème : ${opt.label}. Cliquer pour changer`);
  btn.title = `Thème : ${opt.label}`;
}

/** AbortController du menu compte, hors propriété DOM (évite cast HTMLElement fragile). */
const navAccountAborts = new WeakMap<HTMLElement, AbortController>();

/** Affiche le lien Connexion, ou le menu compte (hover desktop / tap mobile). */
export function renderNavAccount(): void {
  const host = document.getElementById('app-nav-account');
  if (!host) return;
  navAccountAborts.get(host)?.abort();
  const abort = new AbortController();
  navAccountAborts.set(host, abort);
  const { signal } = abort;

  host.innerHTML = '';
  host.classList.add('app-nav-account');

  const user = getCurrentUser();
  if (!user) {
    const link = document.createElement('a');
    link.href = '#connexion';
    link.className = 'app-nav-account-link';
    link.textContent = 'Connexion';
    link.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.hash = 'connexion';
    });
    host.appendChild(link);
    applyRoleVisibility();
    return;
  }

  const menu = document.createElement('div');
  menu.className = 'menu-select menu-select--nav app-nav-account-menu';

  const item = document.createElement('div');
  item.className = 'menu-select__item';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'menu-select__trigger app-nav-account-menu-btn';
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', 'Menu du compte');

  const labelEl = document.createElement('span');
  labelEl.className = 'menu-select__label app-nav-account-info';
  labelEl.textContent = `${user.email} · ${ROLE_LABELS[user.role] ?? user.role}`;
  trigger.append(labelEl, createChevron());

  const dropdown = document.createElement('div');
  dropdown.className = 'menu-select__submenu app-nav-account-dropdown';
  dropdown.setAttribute('role', 'menu');
  dropdown.setAttribute('aria-hidden', 'true');

  const themeLabel = document.createElement('p');
  themeLabel.className = 'app-nav-account-dropdown-label';
  themeLabel.textContent = 'Thème';

  const themeCycle = document.createElement('button');
  themeCycle.type = 'button';
  themeCycle.className = 'menu-select__option app-nav-account-theme-cycle';
  themeCycle.setAttribute('role', 'menuitem');
  themeCycle.innerHTML =
    '<span class="app-nav-account-theme-cycle-icon" aria-hidden="true"></span>' +
    '<span class="app-nav-account-theme-cycle-label"></span>';
  themeCycle.addEventListener('click', (e) => {
    e.stopPropagation();
    applyThemePreference(nextThemePreference(getThemePreference()));
    syncThemeCycleButton(host);
  });

  const adminSection = document.createElement('div');
  adminSection.className = 'app-nav-account-dropdown-section';
  adminSection.dataset.role = 'admin';

  const adminLabel = document.createElement('p');
  adminLabel.className = 'app-nav-account-dropdown-label';
  adminLabel.textContent = 'Administration';

  const accountsLink = document.createElement('a');
  accountsLink.href = '#comptes';
  accountsLink.className = 'menu-select__option app-nav-account-dropdown-item';
  accountsLink.setAttribute('role', 'menuitem');
  accountsLink.textContent = 'Comptes';
  accountsLink.addEventListener('click', (e) => {
    e.preventDefault();
    setMenuOpen(menu, trigger, false);
    window.location.hash = 'comptes';
  });

  const boardLink = document.createElement('a');
  boardLink.href = '#plateau-reglages';
  boardLink.className = 'menu-select__option app-nav-account-dropdown-item';
  boardLink.setAttribute('role', 'menuitem');
  boardLink.textContent = 'Plateau';
  boardLink.addEventListener('click', (e) => {
    e.preventDefault();
    setMenuOpen(menu, trigger, false);
    window.location.hash = 'plateau-reglages';
  });

  const quickRace = document.createElement('a');
  quickRace.href = '#course';
  quickRace.className = 'menu-select__option app-nav-account-dropdown-item';
  quickRace.setAttribute('role', 'menuitem');
  quickRace.textContent = 'Course de debug';
  quickRace.addEventListener('click', (e) => {
    e.preventDefault();
    setMenuOpen(menu, trigger, false);
    window.location.hash = 'course';
  });

  adminSection.append(adminLabel, accountsLink, boardLink, quickRace);

  const logoutBtn = document.createElement('button');
  logoutBtn.type = 'button';
  logoutBtn.className = 'menu-select__option app-nav-account-logout app-nav-account-dropdown-item';
  logoutBtn.setAttribute('role', 'menuitem');
  logoutBtn.textContent = 'Déconnexion';
  logoutBtn.addEventListener('click', () => {
    setMenuOpen(menu, trigger, false);
    logout()
      .then(() => {
        renderNavAccount();
        window.location.hash = 'connexion';
      })
      .catch(() => {
        renderNavAccount();
        window.location.hash = 'connexion';
      });
  });

  dropdown.append(themeLabel, themeCycle, adminSection, logoutBtn);
  item.append(trigger, dropdown);
  menu.appendChild(item);

  let leaveTimer = 0;
  const openMenu = (): void => {
    window.clearTimeout(leaveTimer);
    syncThemeCycleButton(host);
    setMenuOpen(menu, trigger, true);
  };
  const closeMenu = (): void => {
    window.clearTimeout(leaveTimer);
    setMenuOpen(menu, trigger, false);
  };
  const scheduleClose = (): void => {
    window.clearTimeout(leaveTimer);
    leaveTimer = window.setTimeout(closeMenu, 120);
  };

  const hoverMode = prefersHoverMenu();
  menu.dataset.openMode = hoverMode ? 'hover' : 'tap';

  if (hoverMode) {
    menu.addEventListener('pointerenter', openMenu, { signal });
    menu.addEventListener('pointerleave', scheduleClose, { signal });
  } else {
    // Tactile : fermer si on tape ailleurs.
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!menu.classList.contains('is-open')) return;
        if (menu.contains(e.target as Node)) return;
        closeMenu();
      },
      { capture: true, signal }
    );
  }

  trigger.addEventListener(
    'click',
    (e) => {
      e.stopPropagation();
      if (menu.classList.contains('is-open')) closeMenu();
      else openMenu();
    },
    { signal }
  );
  trigger.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape') {
        closeMenu();
        trigger.focus();
      }
      if (e.key === 'ArrowDown' && !menu.classList.contains('is-open')) {
        e.preventDefault();
        openMenu();
      }
    },
    { signal }
  );

  host.appendChild(menu);
  syncThemeCycleButton(host);
  applyRoleVisibility();
}

/** Cache/affiche tout element [data-role] selon le role courant. */
export function applyRoleVisibility(): void {
  const role = getCurrentUser()?.role ?? null;
  document.querySelectorAll<HTMLElement>('[data-role]').forEach((elt) => {
    const allowed = (elt.dataset.role ?? '').split(',').map((r) => r.trim());
    const visible = !!role && allowed.includes(role);
    elt.hidden = !visible;
    // Certains boutons (.btn-pill) font all: unset + display: inline-flex,
    // ce qui l'emporte sur [hidden] { display: none } de l'UA — on force donc
    // aussi le style inline, qui a toujours priorite.
    elt.style.display = visible ? '' : 'none';
  });
}
